import { useLayoutEffect, type RefObject } from "react";

/** A widget showing headline figures: its row may grow with the screen too. */
const METRIC_CONTENT = "[data-metric-size], .density-metric-padding";

/** How much a KPI row may grow beyond its content, as a share of its own height. */
const DEFAULT_METRIC_GROW = 0.2;

/**
 * - `fill`: a row holding a widget with a height weight (`data-fit-weight`, ×1 to
 *   ×4: charts, tables and timelines ×3 and lists that spread over their row ×2
 *   unless the author chose); the row takes the largest weight of its widgets and
 *   shares the free height in proportion.
 * - `metric`: KPI figures without a weight; may grow a little. `auto`: natural height.
 */
export type FitRowKind = "fill" | "metric" | "auto";

export interface FitRow {
  kind: FitRowKind;
  /** The row's natural height, in pixels. */
  height: number;
  /** A fill row's share of the free height, relative to the others (1 by default). */
  weight?: number;
}

/**
 * The row template of a fitted grid, given the height left free under its
 * natural layout. Rows with a weight share the screen in proportion to their
 * weights — relative values, never a total to reach (3 and 2 split 60/40) — and
 * none shrinks below its content. A row of KPI figures only takes a little of
 * it — at most `metricGrow` of its own height, and never more than a quarter of
 * the free height between them — so its tiles breathe without crushing the
 * chart below. Other rows keep their natural height. Without free height (the
 * content overflows) every row keeps its natural height: weighted rows of an
 * unbounded grid would line up on the tallest of them and overflow further.
 */
export function fitRowTemplate(
  rows: readonly FitRow[],
  free: number,
  metricGrow = DEFAULT_METRIC_GROW,
  /**
   * Sections side by side share their row heights (`height` is the tallest of the
   * same row across them): every row keeps it as a floor, so the rows line up
   * whatever each section's own content.
   */
  floors = false,
) {
  const natural = (floor: number) => (floors ? `minmax(${floor}px, auto)` : "auto");
  const grow = Math.min(
    0.5,
    Math.max(0, Number.isFinite(metricGrow) ? metricGrow : DEFAULT_METRIC_GROW),
  );
  const metrics = rows.filter((row) => row.kind === "metric").length;
  const room = Math.max(0, free) / 4 / Math.max(1, metrics);
  return rows
    .map((row) => {
      const floor = Math.ceil(row.height);
      if (row.kind === "fill") {
        if (free <= 0) return natural(floor);
        const weight = row.weight && row.weight > 0 ? row.weight : 1;
        return `minmax(${floors ? `${floor}px` : "min-content"}, ${weight}fr)`;
      }
      if (row.kind === "metric" && grow > 0 && room > 0)
        return `${Math.round(row.height + Math.min(row.height * grow, room))}px`;
      return natural(floor);
    })
    .join(" ");
}

/** A widget covering several rows (`row-span-*`): it stretches with them instead of sizing one. */
function spansRows(cell: HTMLElement): boolean {
  const end = getComputedStyle(cell).gridRowEnd;
  return end.startsWith("span") && end !== "span 1";
}

/** A row's own height: the tallest of the widgets it holds alone. */
function rowHeight(cells: readonly HTMLElement[]): number {
  return Math.max(0, ...cells.filter((cell) => !spansRows(cell)).map((cell) => cell.offsetHeight));
}

/**
 * Where a dashboard is fitted:
 * - `presentation` (full screen, kiosk): `scrollRef` is the presented page's own
 *   scroller. The grid always takes the compact floors and fills the screen;
 *   content that still cannot fit scrolls.
 * - `page` (the standard view): the document scrolls. The grid fills the window
 *   only when its whole content fits at the compact floors; otherwise it is left
 *   as composed, with its usual minimums, and the page scrolls.
 */
export type FitMode = "presentation" | "page";

/**
 * Dashboard fit: a dashboard fills the screen instead of leaving an empty band
 * below it or pushing its last chart below the fold. Opt-in per dashboard
 * (`composition.fitToScreen`, marked `data-fit-to-screen` in the content); any
 * other dashboard keeps its natural height and scrolls.
 *
 * When the content is one widget grid (one section) — or several
 * sections side by side on one row, each filling down to the same bottom
 * line — its rows are grouped as laid out; rows holding a widget with a height weight
 * (`data-fit-weight`) share the height left in proportion to their largest weight,
 * rows of KPI figures may grow a little (`--presentation-metric-grow`,
 * 0 by default, see `fitRowTemplate`; their cells get `data-fit-row="metric"`), the
 * others keep their natural height. Chart minimums drop to the compact floor while
 * fitted (`data-fit-screen`, on the scroller in presentation, on the content in
 * the page). The floor never crops a widget. Stacked sections, or no weighted row,
 * leave the layout untouched.
 */
export function useFitToScreen(
  scrollRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
  mode: FitMode = "presentation",
  enabled = true,
) {
  useLayoutEffect(() => {
    const page = mode === "page";
    const content = contentRef.current;
    const scroller = page ? content : scrollRef.current;
    if (!enabled || !scroller || !content || typeof ResizeObserver === "undefined") return;
    // The element carrying `data-fit-screen` (the compact floors apply below it).
    const marker = scroller;
    const viewport = {
      top: () => (page ? 0 : scroller.getBoundingClientRect().top),
      scrollTop: () => (page ? window.scrollY : scroller.scrollTop),
      height: () => (page ? document.documentElement.clientHeight : scroller.clientHeight),
    };
    let fitted: HTMLElement[] = [];
    let marked: HTMLElement[] = [];
    const observed = new Set<Element>();

    const unmark = () => {
      for (const cell of marked) delete cell.dataset.fitRow;
      marked = [];
    };
    const reset = (grids: readonly HTMLElement[]) => {
      for (const grid of grids) {
        grid.style.gridTemplateRows = "";
        grid.style.minHeight = "";
        grid.style.height = "";
      }
    };
    const release = () => {
      reset(fitted);
      unmark();
      fitted = [];
      delete marker.dataset.fitScreen;
    };

    /** A grid's rows, grouped as laid out, with their weight and what shows figures. */
    const gridRows = (grid: HTMLElement) => {
      const rows: { top: number; weight: number; metric: boolean; cells: HTMLElement[] }[] = [];
      for (const cell of grid.children as HTMLCollectionOf<HTMLElement>) {
        if (!observed.has(cell)) {
          observed.add(cell);
          resizer.observe(cell);
        }
        if (cell.offsetParent === null) continue;
        const top = cell.offsetTop;
        let row = rows.find((candidate) => Math.abs(candidate.top - top) < 2);
        if (!row) rows.push((row = { top, weight: 0, metric: false, cells: [] }));
        row.cells.push(cell);
        if (spansRows(cell)) continue;
        // A row takes the largest height weight of its widgets.
        const weight = Number(cell.dataset.fitWeight);
        if (weight > 0) row.weight = Math.max(row.weight, weight);
        else if (cell.querySelector(METRIC_CONTENT)) row.metric = true;
      }
      return rows.sort((left, right) => left.top - right.top);
    };

    const fit = () => {
      // Only a dashboard that asks for it (`composition.fitToScreen`) is fitted.
      if (!content.querySelector("[data-fit-to-screen]")) return release();
      const grids = [...content.querySelectorAll<HTMLElement>("[data-dashboard-grid]")];
      if (!grids.length) return release();
      // One section, or sections side by side on a single row (a comparison):
      // each grid fills down to the same bottom line. Stacked sections are left as composed.
      const tops = grids.map(
        (grid) => (grid.closest("[data-dashboard-section]") ?? grid).getBoundingClientRect().top,
      );
      if (tops.some((top) => Math.abs(top - tops[0]) > 2)) return release();
      // Measure the natural layout first, with the compact chart floor applied.
      marker.dataset.fitScreen = "";
      reset([...new Set([...fitted, ...grids])]);
      unmark();
      const measured = grids.map((grid) => ({ grid, rows: gridRows(grid) }));
      if (!measured.some(({ rows }) => rows.some((row) => row.weight > 0))) return release();
      // Sections side by side: the n-th rows share the tallest height and the largest
      // weight of any of them (one side's chart weighs the other side's row too).
      const shared = measured.length > 1;
      const aligned: { weight: number; metric: boolean; height: number }[] = [];
      for (const { rows } of measured)
        rows.forEach((row, index) => {
          const height = rowHeight(row.cells);
          const entry = (aligned[index] ??= { weight: 0, metric: false, height: 0 });
          entry.weight = Math.max(entry.weight, row.weight);
          entry.metric ||= row.metric;
          entry.height = Math.max(entry.height, height);
        });
      const scrollerTop = viewport.top();
      const lowest = Math.max(...grids.map((grid) => grid.getBoundingClientRect().bottom));
      // A side list beside the pages takes the screen's height: what it adds below the
      // page's own column is not content after the grids.
      const column = grids[0].closest<HTMLElement>("[data-page-content]");
      const besideList = column?.parentElement;
      const overshoot =
        column && besideList
          ? Math.max(
              0,
              besideList.getBoundingClientRect().bottom - column.getBoundingClientRect().bottom,
            )
          : 0;
      const trailing = Math.max(0, content.getBoundingClientRect().bottom - lowest - overshoot);
      const metricGrow = Number.parseFloat(
        getComputedStyle(scroller).getPropertyValue("--presentation-metric-grow"),
      );
      fitted = [];
      const availableFor = (grid: HTMLElement) => {
        const gridTop = grid.getBoundingClientRect().top - scrollerTop + viewport.scrollTop();
        return Math.floor(viewport.height() - gridTop - trailing);
      };
      // Side by side, every grid fills down to the same line: the shortest room wins,
      // so a subpixel difference between section headers never shifts one side.
      const sharedAvailable = shared ? Math.min(...grids.map(availableFor)) : 0;
      const planned = measured.map(({ grid, rows }) => {
        const kinds = rows.map((row, index) => {
          const source = shared ? aligned[index] : row;
          return {
            weight: source.weight,
            metric: source.metric,
            height: shared ? aligned[index].height : rowHeight(row.cells),
            own: rowHeight(row.cells),
          };
        });
        // The grid's height once its rows take the shared heights.
        const natural =
          grid.offsetHeight + kinds.reduce((sum, row) => sum + row.height - row.own, 0);
        return { grid, rows, kinds, natural };
      });
      // Side by side, the free height is common too (rounding never splits the rows).
      const sharedNatural = Math.max(...planned.map((plan) => plan.natural));
      // The standard view fits only a dashboard that fits whole; a longer one keeps
      // its usual minimums and scrolls.
      if (
        page &&
        planned.some(
          ({ grid, natural }) =>
            (shared ? sharedNatural : natural) > (shared ? sharedAvailable : availableFor(grid)),
        )
      )
        return release();
      for (const { grid, rows, kinds, natural } of planned) {
        if (!kinds.some((row) => row.weight > 0)) continue;
        const available = shared ? sharedAvailable : availableFor(grid);
        grid.style.gridTemplateRows = fitRowTemplate(
          kinds.map((row) => ({
            kind: row.weight > 0 ? "fill" : row.metric ? "metric" : "auto",
            height: row.height,
            weight: row.weight,
          })),
          available - (shared ? sharedNatural : natural),
          metricGrow,
          shared,
        );
        for (const [index, row] of rows.entries()) {
          if (kinds[index].weight > 0 || !kinds[index].metric) continue;
          for (const cell of row.cells) cell.dataset.fitRow = "metric";
          marked.push(...row.cells);
        }
        // When everything fits, the grid takes exactly the room left: a definite height
        // lets CSS share it by the rows' weights. A grid only given a minimum sizes its
        // weighted rows as if unbounded (each `fr` as large as the tallest row needs),
        // which overflowed the screen as soon as a lighter row outgrew a heavier one.
        const fits = (shared ? sharedNatural : natural) <= available;
        grid.style[fits ? "height" : "minHeight"] = `${Math.max(0, available)}px`;
        fitted.push(grid);
      }
    };

    const schedule = fit;
    const resizer = new ResizeObserver(schedule);
    resizer.observe(scroller);
    if (page) window.addEventListener("resize", schedule);
    const mutations = new MutationObserver(schedule);
    mutations.observe(content, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "data-layout-kind",
        "data-fit-weight",
        "data-metric-size",
        "data-fit-to-screen",
      ],
    });
    fit();
    return () => {
      resizer.disconnect();
      mutations.disconnect();
      if (page) window.removeEventListener("resize", schedule);
      release();
    };
  }, [scrollRef, contentRef, mode, enabled]);
}
